/**
 * AR Playbook Super Admin API
 *
 * API endpoints EXCLUSIVELY for SUPER_ADMIN role functionality
 * This file should ONLY be imported and used in super admin contexts
 *
 * Features:
 * - Business user management (view all, suspend, delete)
 * - Ghost login / impersonation
 * - Student management (view all platform students)
 * - License management (view/edit all platform licenses)
 * - Platform-wide analytics and statistics
 * - 3D Model asset management
 * - Archived students management
 *
 * ⚠️ SECURITY: All endpoints require SUPER_ADMIN role on backend
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { host } from '../constants/apiconfig'
import { IModel } from '../modules/arplaybook/businessuser/types'
import { TBusinessUserRole } from '../modules/arplaybook/types/businessUserRole'
import { TCurrency } from '../types/marketplace'
import { BUSINESS_USER_TOKEN, businessUserBaseQueryWithAuth } from './arPlaybookBaseQuery'

// ============================================
// Types
// ============================================

export interface IPlatformFeeSettings {
    platformFeePercent: number
    minimumPayout: number
}

export interface IBusinessUserAdmin {
    _id: string
    name: string
    username: string
    institutionName: string
    subdomain: string
    licenseLimit: number
    role: TBusinessUserRole
    isSuspended: boolean
    totalLicenses: number
    activeLicenses: number
    pendingLicenses: number
    platformFeeSettings?: IPlatformFeeSettings
    currency?: TCurrency
    createdAt: string
    updatedAt: string
}

export interface IBusinessUserDetail extends IBusinessUserAdmin {
    licenses: ILicenseAdmin[]
    authSettings: {
        defaultMethod: string
        allowedMethods: string[]
    }
    studentCount: number
    playbookCount: number
    playBookLimit: number
    maxModelsPerPlayBook: number
}

export interface IGetBusinessUserByIdResponse {
    user: IBusinessUserDetail
}

export interface IStudentAdmin {
    _id: string
    studentName: string
    createdAt: string
    licenses: ILicenseAdmin[]
    businessUsers: IBusinessUserAdmin[]
}

export interface ILicenseAdmin {
    _id: string
    licenseKey: string
    licenseNo: number
    status: 'unassigned' | 'pending' | 'active' | 'expired'
    maxDevices: number
    devices: string[]
    creationDate: string
    activationDate?: string
    expiryDate?: string
    book?: {
        _id: string
        name: string
    }
    business?: {
        _id: string
        name: string
        institutionName: string
    }
    student?: {
        _id: string
        studentName: string
    }
}

export interface IPagination {
    total: number
    page: number
    limit: number
    totalPages: number
}

export interface IGetBusinessUsersResponse {
    users: IBusinessUserAdmin[]
    pagination: IPagination
}

export interface IGetStudentsResponse {
    students: IStudentAdmin[]
    pagination: IPagination
}

export interface IGetLicensesResponse {
    licenses: ILicenseAdmin[]
    pagination: IPagination
}

export interface IStatsResponse {
    totalBusinessUsers: number
    totalStudents: number
    totalLicenses: number
    licensesByStatus: {
        activeNumbers: number[]
        pendingNumbers: number[]
        expiredNumbers: number[]
    }
    suspendedUsers: number
    recentActivity: {
        newBusinessUsers: number
        newStudents: number
    }
}

export interface IUsageSummaryItem {
    _id: string
    name: string
    username: string
    institutionName: string
    licenseLimit: number
    usedLicenses: number
    remainingLicenses: number
    usagePercentage: number
}

export interface IUsageSummaryResponse {
    usageSummary: IUsageSummaryItem[]
}

export interface IGhostLoginResponse {
    token: string
    user: {
        id: string
        name: string
        username: string
        institutionName: string
        subdomain: string
        role: TBusinessUserRole
    }
}

export interface IUpdateBusinessUserRequest {
    id: string
    updates: {
        name?: string
        institutionName?: string
        licenseLimit?: number
        playBookLimit?: number
        maxModelsPerPlayBook?: number
        role?: TBusinessUserRole
        authSettings?: {
            defaultMethod: string
            allowedMethods: string[]
        }
        platformFeeSettings?: {
            platformFeePercent?: number
            minimumPayout?: number
        }
        currency?: TCurrency
    }
}

export interface IUpdateLicenseRequest {
    licenseId: string
    status?: 'unassigned' | 'pending' | 'active' | 'expired'
    expiryDate?: string
    maxDevices?: number
}

// ============================================
// Query Args
// ============================================

export interface IGetBusinessUsersArgs {
    page?: number
    limit?: number
    search?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
}

export interface IGetStudentsArgs {
    page?: number
    limit?: number
    search?: string
    businessUserId?: string
}

export interface IGetLicensesArgs {
    page?: number
    limit?: number
    status?: 'unassigned' | 'pending' | 'active' | 'expired'
    businessUserId?: string
    bookId?: string
}

// ============================================
// Super Admin Analytics Types
// ============================================

export interface ISuperAdminAnalyticsQuery {
    startDate?: string
    endDate?: string
    businessUserId?: string
}

export interface IAnalyticsOverview {
    totalSessions: number | 'Error'
    uniqueUsers: number | 'Error'
    totalPageViews: number | 'Error'
    totalARLaunches: number | 'Error'
    avgSessionDuration: number | 'Error'
}

export interface ISessionData {
    date: string
    sessions: number
    users: number
}

export interface IDeviceData {
    device: string
    users: number
}

export interface ISuperAdminAnalytics {
    overview: IAnalyticsOverview
    sessionsOverTime: ISessionData[] | 'Error'
    deviceBreakdown: IDeviceData[] | 'Error'
}

// ============================================
// Archived Students Types
// ============================================

export interface IArchivedLicense {
    licenseKey: string
    licenseNo: number
    status: string
    bookName?: string
    activationDate?: string
    expiryDate?: string
}

export interface IArchivedBusinessUser {
    _id: string
    name: string
    username: string
    institutionName: string
    subdomain: string
}

export interface IArchivedStudent {
    _id: string
    originalStudentId: string
    studentName: string
    relationType: 'mother' | 'father' | 'guardian'
    relationName: string
    email: string
    phone: number
    studentCreatedAt: string
    businessUser: IArchivedBusinessUser
    licenses: IArchivedLicense[]
    archivedAt: string
    archivedBy: string
    archiveReason: string
    notes?: string
}

export interface IGetArchivedStudentsResponse {
    archivedStudents: IArchivedStudent[]
    pagination: IPagination
}

export interface IGetArchivedStudentsArgs {
    page?: number
    limit?: number
    search?: string
    businessUserId?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
}

export interface IArchivedStudentStatsResponse {
    total: number
    byMonth: Array<{
        _id: { year: number; month: number }
        count: number
    }>
    byBusinessUser: Array<{
        _id: string
        businessName: string
        institutionName: string
        count: number
    }>
}

// ============================================
// Storage Migration Types
// ============================================

export type TStorageMigrationStatus =
    | 'not_started'
    | 'pending'
    | 'in_progress'
    | 'completed'
    | 'failed'

export interface IMigrationDashboard {
    total: number
    completed: number
    inProgress: number
    pending: number
    failed: number
    notStarted: number
    percentComplete: number
}

export interface IMigrationQueueUser {
    _id: string
    name: string
    username: string
    subdomain: string
    institutionName: string
    storageBucket?: string
    storageMigrationStatus?: TStorageMigrationStatus
    storageMigrationStartedAt?: string
    storageMigrationCompletedAt?: string
    createdAt: string
    updatedAt: string
}

export interface IMigrationQueueResponse {
    users: IMigrationQueueUser[]
    pagination: IPagination
}

export interface IMigrationProgress {
    totalFiles: number
    copiedFiles: number
    failedFiles: number
    status: TStorageMigrationStatus
    startedAt?: string
    completedAt?: string
    errors: string[]
    cursor?: number
    hasMore?: boolean
}

export interface IUserMigrationStatus {
    userId: string
    status: TStorageMigrationStatus
    bucketName?: string
    startedAt?: string
    completedAt?: string
}

export interface IMigrateUserResponse {
    message: string
    userId: string
    progress: IMigrationProgress
}

export interface IPreviewUrlMigrationResponse {
    userId: string
    preview: {
        urlsToTransform: number
        sampleUrls: Array<{ original: string; transformed: string }>
    }
}

// ============================================
// API Definition
// ============================================

export const arPlaybookSuperAdminApi = createApi({
    reducerPath: 'arPlaybookSuperAdminApi',
    baseQuery: businessUserBaseQueryWithAuth,
    tagTypes: [
        'SuperAdminBusinessUsers',
        'SuperAdminStudents',
        'SuperAdminLicenses',
        'SuperAdminStats',
        'SuperAdminArchivedStudents',
        'SuperAdminModelAssets',
        'SuperAdminMigration',
    ],
    endpoints: builder => ({
        getAllBusinessUsers: builder.query<IGetBusinessUsersResponse, IGetBusinessUsersArgs>({
            query: ({
                page = 1,
                limit = 20,
                search = '',
                sortBy = 'createdAt',
                sortOrder = 'desc',
            }) => ({
                url: `${host}ar-playbook/admin/business-users`,
                params: { page, limit, search, sortBy, sortOrder },
            }),
            providesTags: ['SuperAdminBusinessUsers'],
        }),

        getBusinessUserById: builder.query<IGetBusinessUserByIdResponse, string>({
            query: userId => `${host}ar-playbook/admin/business-users/${userId}`,
            providesTags: (_result, _error, userId) => [
                { type: 'SuperAdminBusinessUsers', id: userId },
            ],
        }),

        updateBusinessUser: builder.mutation<
            { message: string; user: IBusinessUserAdmin },
            IUpdateBusinessUserRequest
        >({
            query: ({ id, updates }) => ({
                url: `${host}ar-playbook/admin/business-users/${id}`,
                method: 'PUT',
                body: updates,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                'SuperAdminBusinessUsers',
                { type: 'SuperAdminBusinessUsers', id },
            ],
        }),

        suspendBusinessUser: builder.mutation<{ message: string }, string>({
            query: userId => ({
                url: `${host}ar-playbook/admin/business-users/${userId}/suspend`,
                method: 'POST',
            }),
            invalidatesTags: ['SuperAdminBusinessUsers'],
        }),

        restoreBusinessUser: builder.mutation<{ message: string }, string>({
            query: userId => ({
                url: `${host}ar-playbook/admin/business-users/${userId}/restore`,
                method: 'POST',
            }),
            invalidatesTags: ['SuperAdminBusinessUsers'],
        }),

        deleteBusinessUser: builder.mutation<
            {
                message: string
                deleted: {
                    user: string
                    username: string
                    licenses: number
                    books: number
                    theme: boolean
                    students: number
                }
                archived: {
                    students: number
                }
            },
            { userId: string; skipArchive?: boolean }
        >({
            query: ({ userId, skipArchive }) => ({
                url: `${host}ar-playbook/admin/business-users/${userId}/delete`,
                method: 'DELETE',
                body: { confirmDelete: 'PERMANENTLY_DELETE', skipArchive },
            }),
            invalidatesTags: [
                'SuperAdminBusinessUsers',
                'SuperAdminStudents',
                'SuperAdminLicenses',
                'SuperAdminStats',
                'SuperAdminArchivedStudents',
            ],
        }),

        bulkDeleteBusinessUsers: builder.mutation<
            {
                message: string
                deleted: { userId: string; name: string; username: string }[]
                failed: { userId: string; reason: string }[]
                totalLicensesDeleted: number
                totalBooksDeleted: number
                totalStudentsArchived: number
                totalStudentsDeleted: number
            },
            { userIds: string[]; skipArchive?: boolean }
        >({
            query: ({ userIds, skipArchive }) => ({
                url: `${host}ar-playbook/admin/business-users/bulk-delete`,
                method: 'POST',
                body: { userIds, confirmDelete: 'PERMANENTLY_DELETE_ALL', skipArchive },
            }),
            invalidatesTags: [
                'SuperAdminBusinessUsers',
                'SuperAdminStudents',
                'SuperAdminLicenses',
                'SuperAdminStats',
                'SuperAdminArchivedStudents',
            ],
        }),

        // ============================================
        // Ghost Login (SUPER_ADMIN ONLY)
        // ============================================

        ghostLogin: builder.query<IGhostLoginResponse, string>({
            query: userId => `${host}ar-playbook/admin/ghost-login/${userId}`,
        }),

        // ============================================
        // Student Management (SUPER_ADMIN ONLY)
        // ============================================

        getAllStudents: builder.query<IGetStudentsResponse, IGetStudentsArgs>({
            query: ({ page = 1, limit = 20, search = '', businessUserId }) => ({
                url: `${host}ar-playbook/admin/students`,
                params: { page, limit, search, ...(businessUserId && { businessUserId }) },
            }),
            providesTags: ['SuperAdminStudents'],
        }),

        // ============================================
        // Archived Students Management (SUPER_ADMIN ONLY)
        // ============================================

        getArchivedStudents: builder.query<IGetArchivedStudentsResponse, IGetArchivedStudentsArgs>({
            query: ({
                page = 1,
                limit = 20,
                search = '',
                businessUserId,
                sortBy = 'archivedAt',
                sortOrder = 'desc',
            }) => ({
                url: `${host}ar-playbook/admin/archived-students`,
                params: {
                    page,
                    limit,
                    search,
                    sortBy,
                    sortOrder,
                    ...(businessUserId && { businessUserId }),
                },
            }),
            providesTags: ['SuperAdminArchivedStudents'],
        }),

        getArchivedStudentStats: builder.query<IArchivedStudentStatsResponse, void>({
            query: () => `${host}ar-playbook/admin/archived-students/stats`,
            providesTags: ['SuperAdminArchivedStudents'],
        }),

        exportArchivedStudents: builder.query<string, { businessUserId?: string }>({
            query: ({ businessUserId }) => ({
                url: `${host}ar-playbook/admin/archived-students/export`,
                params: businessUserId ? { businessUserId } : {},
                responseHandler: response => response.text(),
            }),
        }),

        deleteArchivedStudent: builder.mutation<{ message: string }, string>({
            query: archivedStudentId => ({
                url: `${host}ar-playbook/admin/archived-students/${archivedStudentId}`,
                method: 'DELETE',
                body: { confirmDelete: 'PERMANENTLY_DELETE_ARCHIVED' },
            }),
            invalidatesTags: ['SuperAdminArchivedStudents'],
        }),

        // ============================================
        // License Management (SUPER_ADMIN ONLY - All Platform Licenses)
        // ============================================

        getAllLicenses: builder.query<IGetLicensesResponse, IGetLicensesArgs>({
            query: ({ page = 1, limit = 20, status, businessUserId, bookId }) => ({
                url: `${host}ar-playbook/admin/licenses`,
                params: {
                    page,
                    limit,
                    ...(status && { status }),
                    ...(businessUserId && { businessUserId }),
                    ...(bookId && { bookId }),
                },
            }),
            providesTags: ['SuperAdminLicenses'],
        }),

        updateLicense: builder.mutation<
            { message: string; license: ILicenseAdmin },
            IUpdateLicenseRequest
        >({
            query: ({ licenseId, ...body }) => ({
                url: `${host}ar-playbook/admin/licenses/${licenseId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['SuperAdminLicenses'],
        }),

        freeLicense: builder.mutation<{ message: string; license: ILicenseAdmin }, string>({
            query: licenseId => ({
                url: `${host}ar-playbook/admin/licenses/${licenseId}/free`,
                method: 'POST',
            }),
            invalidatesTags: ['SuperAdminLicenses'],
        }),

        // ============================================
        // Analytics & Stats (SUPER_ADMIN ONLY)
        // ============================================

        getStats: builder.query<IStatsResponse, void>({
            query: () => `${host}ar-playbook/admin/stats`,
            providesTags: ['SuperAdminStats'],
        }),

        getUsageSummary: builder.query<IUsageSummaryResponse, void>({
            query: () => `${host}ar-playbook/admin/usage-summary`,
        }),

        getSuperAdminAnalytics: builder.query<
            ISuperAdminAnalytics,
            ISuperAdminAnalyticsQuery | void
        >({
            query: (params = {}) => {
                const queryParams = new URLSearchParams()
                if (params && 'startDate' in params && params.startDate) {
                    queryParams.append('startDate', params.startDate)
                }
                if (params && 'endDate' in params && params.endDate) {
                    queryParams.append('endDate', params.endDate)
                }
                if (params && 'businessUserId' in params && params.businessUserId) {
                    queryParams.append('businessUserId', params.businessUserId)
                }
                const queryString = queryParams.toString()
                return {
                    url: `${host}ar-playbook/admin/analytics${queryString ? `?${queryString}` : ''}`,
                    method: 'GET',
                }
            },
        }),

        // ============================================
        // 3D Model Asset Management (SUPER_ADMIN ONLY)
        // ============================================

        getModelAssets: builder.query<IModel[], void>({
            query: () => ({
                url: `${host}ar-playbook/admin/model-assets`,
                method: 'GET',
            }),
            providesTags: ['SuperAdminModelAssets'],
        }),

        uploadModelAsset: builder.mutation<
            void,
            { url: string; name: string; categories: string[] }
        >({
            query: body => ({
                url: `${host}ar-playbook/admin/model-assets`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['SuperAdminModelAssets'],
        }),

        deleteModelAsset: builder.mutation<void, IModel>({
            query: body => ({
                url: `${host}ar-playbook/admin/model-assets`,
                method: 'DELETE',
                body,
            }),
            invalidatesTags: ['SuperAdminModelAssets'],
        }),

        updateModelAsset: builder.mutation<{ message: string }, IModel>({
            query: body => ({
                url: `${host}ar-playbook/admin/model-assets`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['SuperAdminModelAssets'],
        }),

        // ============================================
        // Storage Migration Management (SUPER_ADMIN ONLY)
        // ============================================

        getMigrationDashboard: builder.query<IMigrationDashboard, void>({
            query: () => `${host}ar-playbook/admin/migration/dashboard`,
            providesTags: ['SuperAdminMigration'],
        }),

        getMigrationQueue: builder.query<
            IMigrationQueueResponse,
            {
                status?: 'pending' | 'failed' | 'in_progress' | 'completed' | 'not_started' | 'all'
                page?: number
                limit?: number
                sortBy?: string
                sortOrder?: 'asc' | 'desc'
            }
        >({
            query: ({
                status = 'pending',
                page = 1,
                limit = 20,
                sortBy = 'createdAt',
                sortOrder = 'asc',
            }) => ({
                url: `${host}ar-playbook/admin/migration/queue`,
                params: { status, page, limit, sortBy, sortOrder },
            }),
            providesTags: ['SuperAdminMigration'],
        }),

        getUserMigrationStatus: builder.query<IUserMigrationStatus, string>({
            query: userId => `${host}ar-playbook/admin/migration/user/${userId}`,
            providesTags: (_result, _error, userId) => [
                { type: 'SuperAdminMigration', id: userId },
            ],
        }),

        previewUserUrlMigration: builder.query<IPreviewUrlMigrationResponse, string>({
            query: userId => `${host}ar-playbook/admin/migration/user/${userId}/urls/preview`,
        }),

        migrateUser: builder.mutation<
            IMigrateUserResponse,
            { userId: string; deleteSource?: boolean }
        >({
            query: ({ userId, deleteSource = false }) => ({
                url: `${host}ar-playbook/admin/migration/user/${userId}`,
                method: 'POST',
                body: { deleteSource },
            }),
            invalidatesTags: (_result, _error, { userId }) => [
                'SuperAdminMigration',
                { type: 'SuperAdminMigration', id: userId },
            ],
        }),

        migrateUserFull: builder.mutation<
            IMigrateUserResponse,
            { userId: string; deleteSource?: boolean }
        >({
            query: ({ userId, deleteSource = false }) => ({
                url: `${host}ar-playbook/admin/migration/user/${userId}/full`,
                method: 'POST',
                body: { deleteSource },
            }),
            invalidatesTags: (_result, _error, { userId }) => [
                'SuperAdminMigration',
                { type: 'SuperAdminMigration', id: userId },
            ],
        }),

        // Async migration endpoint - returns immediately, runs in background
        migrateUserAsync: builder.mutation<
            { message: string; userId: string; taskName?: string; status: string },
            { userId: string; deleteSource?: boolean }
        >({
            query: ({ userId, deleteSource = false }) => ({
                url: `${host}ar-playbook/admin/migration/user/${userId}/async`,
                method: 'POST',
                body: { deleteSource },
            }),
            invalidatesTags: (_result, _error, { userId }) => [
                'SuperAdminMigration',
                { type: 'SuperAdminMigration', id: userId },
            ],
        }),

        // Get migration progress for polling
        getMigrationProgress: builder.query<IMigrationProgress, string>({
            query: userId => ({
                url: `${host}ar-playbook/admin/migration/user/${userId}/progress`,
            }),
            providesTags: (_result, _error, userId) => [
                { type: 'SuperAdminMigration', id: `progress-${userId}` },
            ],
        }),

        resetUserMigration: builder.mutation<{ message: string }, string>({
            query: userId => ({
                url: `${host}ar-playbook/admin/migration/user/${userId}/reset`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, userId) => [
                'SuperAdminMigration',
                { type: 'SuperAdminMigration', id: userId },
            ],
        }),

        initializeMigrationStatus: builder.mutation<{ message: string; updated: number }, void>({
            query: () => ({
                url: `${host}ar-playbook/admin/migration/initialize`,
                method: 'POST',
            }),
            invalidatesTags: ['SuperAdminMigration'],
        }),
    }),
})

// Export hooks for Super Admin only
export const {
    useGetAllBusinessUsersQuery,
    useGetBusinessUserByIdQuery,
    useUpdateBusinessUserMutation,
    useSuspendBusinessUserMutation,
    useRestoreBusinessUserMutation,
    useDeleteBusinessUserMutation,
    useBulkDeleteBusinessUsersMutation,
    useLazyGhostLoginQuery,
    useGetAllStudentsQuery,
    useGetArchivedStudentsQuery,
    useGetArchivedStudentStatsQuery,
    useLazyExportArchivedStudentsQuery,
    useDeleteArchivedStudentMutation,
    useGetAllLicensesQuery,
    useUpdateLicenseMutation,
    useFreeLicenseMutation,
    useGetStatsQuery,
    useGetUsageSummaryQuery,
    useGetSuperAdminAnalyticsQuery,
    useLazyGetSuperAdminAnalyticsQuery,
    useGetModelAssetsQuery,
    useUploadModelAssetMutation,
    useDeleteModelAssetMutation,
    useUpdateModelAssetMutation,
    // Storage Migration hooks
    useGetMigrationDashboardQuery,
    useGetMigrationQueueQuery,
    useGetUserMigrationStatusQuery,
    useLazyPreviewUserUrlMigrationQuery,
    useMigrateUserMutation,
    useMigrateUserFullMutation,
    useMigrateUserAsyncMutation,
    useGetMigrationProgressQuery,
    useLazyGetMigrationProgressQuery,
    useResetUserMigrationMutation,
    useInitializeMigrationStatusMutation,
} = arPlaybookSuperAdminApi

// ============================================
// Ghost Login Helper
// ============================================

/**
 * Opens a new browser tab with the ghost user's session
 */
export const openGhostLoginTab = (token: string, _subdomain?: string) => {
    const newWindow = window.open('')
    if (newWindow && token.length > 0) {
        newWindow.sessionStorage.setItem(BUSINESS_USER_TOKEN, token)
        // Redirect to the business user dashboard
        newWindow.location.href = `${window.location.origin}/admin/dashboard`
    }
}
